import { Component, Input, OnInit } from '@angular/core';
import { Subject } from 'rxjs';
import { debounceTime } from 'rxjs/operators';

import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';

import {
  Customer,
  CustomerSearchResult,
  getGuestPassEligibilityDisplay,
  getGuestPassSourceText,
  GuestPass,
  GuestPassEligibility,
  GuestPassStatus
} from '../../../../../../../../../models/customer';
import { AccountService } from '../../../../../../../../../services/account/account.service';
import { CustomerService } from '../../../../../../../../../services/customer/customer.service';
import { ListResponseCountOptional } from '../../../../../../../../../models/list-response';
import { dateStringFromISO8601, getISO8601DateStringFromMoment, propExists } from '../../../../../../../../../utils';
import { CustomerMembershipStatus } from '../../../../../../../../../models/membership-enums';
import { getCustomerMembershipStatusDisplay } from '../../../../../../../../../models/membership';
import * as moment from 'moment';
import {
  OrganizationConfigService
} from '../../../../../../../../../services/organization-config/organization-config.service';

@Component({
  selector: 'app-choose-check-in-guest-pass-modal',
  templateUrl: './choose-check-in-guest-pass-modal.component.html',
  styleUrls: ['./choose-check-in-guest-pass-modal.component.css']
})
export class ChooseCheckInGuestPassModalComponent implements OnInit {

  @Input() customer: Customer;
  @Input() guestPassChosenCallback: (guestPassId: number) => undefined;

  public loadingCustomers = false;
  public search = '';
  public customerFiltersChangedSubject = new Subject<null>();
  public customerList: ListResponseCountOptional<CustomerSearchResult>;

  public selectedGrantingCustomer: CustomerSearchResult | null = null;

  public loadingGuestPasses = false;
  public grantingCustomerGuestPasses: GuestPass[] = null;
  // Set when the pass list could not be loaded for the customer being
  // checked in (e.g. their profile was removed or merged after this page
  // loaded); shown in place of the list rather than as a server error.
  public guestPassesErrorMessage = '';

  public serverError: number = null;

  // Create instance references to lookup functions and enums so they may be
  // called in the template.
  public readonly CustomerMembershipStatus = CustomerMembershipStatus;
  public readonly dateStringFromISO8601 = dateStringFromISO8601;
  public readonly getGuestPassSourceText = getGuestPassSourceText;
  public readonly getGuestPassEligibilityDisplay = getGuestPassEligibilityDisplay;
  public readonly GuestPassEligibility = GuestPassEligibility;
  public readonly getCustomerMembershipStatusDisplay = getCustomerMembershipStatusDisplay;

  // The number of customer list requests. Used to ensure that only the most
  // recent request is displayed, in the event of concurrent requests (due to,
  // e.g., filtering).
  private customerListRequestCount = 0;

  private recentActivityFilter: string | undefined = undefined;

  constructor(
    public accountService: AccountService,
    public activeModal: NgbActiveModal,
    public orgConfigService: OrganizationConfigService,
    private customerService: CustomerService
  ) {}

  ngOnInit() {
    if (!this.accountService.getMyAccount().can_manage_members) {
      return;
    }

    // Handle changes to the customer filters by reloading the customers,
    // subject to debounce.
    this.customerFiltersChangedSubject.pipe(
      // Avoid rapid requests by waiting 300ms before applying the filters.
      debounceTime(300)
    ).subscribe(() => {
      this.loadCustomers();
    });
  }

  /**
   * Handle a change in the customer filters. The list of customers will be
   * filtered according to the new values.
   */
  public handleCustomerFilterChange() {
    this.loadingCustomers = true;

    this.selectedGrantingCustomer = null;

    const orgConfig = this.orgConfigService.getOrganizationConfig();
    const mustApplyRecentActivityFilter = (
      orgConfig &&
      orgConfig.customer_search_recent_activity_window_months
    );
    if (mustApplyRecentActivityFilter) {
      this.recentActivityFilter = getISO8601DateStringFromMoment(
        moment().subtract(orgConfig.customer_search_recent_activity_window_months, 'months')
      );
    }

    this.customerFiltersChangedSubject.next();
  }

  /**
   * Load the page of customers corresponding to the current page and filter
   * selections.
   */
  public loadCustomers() {
    this.loadingCustomers = true;

    // Increment the request count and save a local copy.
    this.customerListRequestCount++;
    const thisRequestCount = this.customerListRequestCount;

    if (!this.hasSearchInput()) {
      this.customerList = null;
      this.loadingCustomers = false;
      return;
    }

    this.customerService
      .searchCustomers(
        1,
        200,
        {
          search: this.search,
          last_activity_timestamp_min: this.recentActivityFilter,
        },
        true
      )
      .then(response => {
        // Only update the customer list if there has not been a more recent
        // request initiated.
        if (thisRequestCount === this.customerListRequestCount) {
          this.customerList = response;
        }
      })
      .catch(reason => {
        if (reason.status === 403) {
          this.accountService.logout();
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => {
        if (thisRequestCount === this.customerListRequestCount) {
          this.loadingCustomers = false;
        }
      });
  }

  /**
   * Whether a non-zero number of customer results have been loaded.
   */
  public customerResultsPresent(): boolean {
    return (
      this.customerList &&
      this.customerList.results &&
      (this.customerList.results.length > 0)
    );
  }

  public filteredCustomerResults(): CustomerSearchResult[] {
    return this.customerList.results;
  }

  /**
   * Handle a click on one of the customer results by looking up its guest
   * passes and displaying the customer's name in the search input.
   */
  public selectGrantingCustomer(customer: CustomerSearchResult) {
    this.selectedGrantingCustomer = customer;
    this.search = (
      customer.first_name + ' ' + customer.last_name
    );
    this.grantingCustomerGuestPasses = null;
    this.guestPassesErrorMessage = '';

    this.loadingGuestPasses = true;

    this.customerService
      .getGuestPasses(
        1,
        3000,
        {
          owner_id: customer.id,
          status: GuestPassStatus.available,
          // The server fills each pass's ineligibility_reason for the
          // customer being checked in, using the same rule the check-in
          // endpoint enforces (PRO-1419).
          check_in_customer_id: this.customer.id
        },
        true
      )
      .then(response => {
        // Ineligible passes stay visible but disabled, with the reason, so
        // staff can see why a pass is not on offer; eligible ones list first.
        this.grantingCustomerGuestPasses = response.results
          .slice()
          .sort((a, b) => Number(!!a.ineligibility_reason) - Number(!!b.ineligibility_reason));
      })
      .catch(reason => {
        // The server keys its 400 body by parameter; only the customer
        // being checked in failing to resolve (removed or merged since the
        // page loaded) gets the friendly message. Anything else is a
        // genuine error.
        if ((reason.status === 400) && reason.error?.check_in_customer_id) {
          this.guestPassesErrorMessage = (
            'The customer being checked in could not be found. They may have been ' +
            'removed or merged since this page was loaded; reload the page and try again.'
          );
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => { this.loadingGuestPasses = false; });
  }

  public hasSearchInput() {
    return this.search.trim().length >= 3;
  }

  public guestPassMembershipIsInactive(guestPass: GuestPass): boolean {
    return (
      propExists(guestPass.granting_membership_status) &&
      (guestPass.granting_membership_status !== CustomerMembershipStatus.active)
    );
  }

  public guestPassIsUnusable(guestPass: GuestPass): boolean {
    return (
      this.guestPassMembershipIsInactive(guestPass) ||
      !!guestPass.ineligibility_reason
    );
  }

  public hasRecentActivityFilter(): boolean {
    return propExists(this.recentActivityFilter);
  }

  public handleExpandSearch() {
    this.recentActivityFilter = undefined;
    this.loadingCustomers = true;
    this.customerFiltersChangedSubject.next();
  }
}
